import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Control, FormState } from 'react-hook-form';
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as _ from 'lodash';

import OnboardForm, { formClasses } from './OnboardForm';
import TextInput2 from '../form/TextInput2';
import validationUtils from '../../services/validationUtils';
import SelectMenu2 from '../form/SelectMenu2';
import { InsuranceStrength } from '../../data/models/insurance-strength.model';
import DateInput2 from '../form/DateInput2';
import { InsuranceCoverageType } from '../../services/insuranceUtils';
import CheckboxInput2 from '../form/CheckboxInput2';

interface InputItem {
  label: string;
  name: string;
  props: any;
  currency: boolean;
  enabledID?: number;
}

interface TextInputItemProps {
  item: InputItem;
  control: Control;
  formState: FormState<any>;
  required?: boolean;
  requiredFieldMessage: string;
  disabled?: () => boolean;
}

const TextInputItem = ({
  item,
  control,
  formState,
  required = true,
  requiredFieldMessage,
  disabled = () => false,
}: TextInputItemProps) => {
  const { t } = useTranslation('common');
  const rules: any = required ? { required: requiredFieldMessage } : {};
  if (item.currency) {
    rules.validate = validationUtils.rangeValidation(0, 100000000, t);
  }
  return (
    <TextInput2
      className={formClasses.control}
      label={item.label}
      name={item.name}
      type={item.currency ? 'number' : 'text'}
      inputProps={item.props}
      required={required}
      control={control}
      errors={formState.errors}
      rules={rules}
      disabled={disabled()}
    />
  );
};

interface InsuranceFormProps {
  insuranceStrengths: InsuranceStrength[];
  defaultValues: any;
  onSubmit: (values: any) => void;
}

const InsuranceForm = forwardRef(
  (
    { insuranceStrengths, defaultValues, onSubmit }: InsuranceFormProps,
    ref,
  ) => {
    const { t } = useTranslation('common');
    const requiredFieldMessage = t('common.requiredField');

    const [minDate, setMinDate] = useState<DateTime>();
    const [maxDate, setMaxDate] = useState<DateTime>();

    useEffect(() => {
      setMinDate(DateTime.now().minus({ years: 5 }).startOf('year'));
      setMaxDate(DateTime.now().plus({ years: 15 }).endOf('year'));
    }, []);

    const { control, formState, handleSubmit, getValues } = useForm({
      defaultValues: _.pick(defaultValues, [
        'CommercialInsuranceCompanyName',
        'CommercialInsurancePolicyNumber',
        'CommercialInsuranceExpiresOn',
        'CommercialInsuranceStrengthID',
        'AutoInsuranceCompanyName',
        'AutoInsurancePolicyNumber',
        'AutoInsuranceExpiresOn',
        'AutoInsuranceStrengthID',
        'AutoInsuranceCoverageTypeID',
        'HasWorkersComp',
      ]),
    });

    useImperativeHandle(ref, () => ({
      submitForm: handleSubmit(onSubmit),
      getValues,
    }));

    const commercialPolicyItems: InputItem[] = [
      {
        label: t('provider.insuranceCompany'),
        name: 'CommercialInsuranceCompanyName',
        props: { maxLength: 100 },
        currency: false,
      },
      {
        label: t('provider.insurancePolicyNum'),
        name: 'CommercialInsurancePolicyNumber',
        props: { maxLength: 50 },
        currency: false,
      },
    ];

    const autoPolicyItems: InputItem[] = [
      {
        label: t('provider.insuranceCompany'),
        name: 'AutoInsuranceCompanyName',
        props: { maxLength: 100 },
        currency: false,
      },
      {
        label: t('provider.insurancePolicyNum'),
        name: 'AutoInsurancePolicyNumber',
        props: { maxLength: 50 },
        currency: false,
      },
    ];

    const limitTypes = [
      {
        limitTypeID: InsuranceCoverageType.SplitLimits,
        limitTypeName: t('provider.insuranceSplit'),
      },
      {
        limitTypeID: InsuranceCoverageType.CombinedSingleLimit,
        limitTypeName: t('provider.insuranceCombined'),
      },
    ];

    const formCtrl = { control, formState, requiredFieldMessage };

    return (
      <OnboardForm title={t('onboard.insurance')}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Typography className={formClasses.subtitle}>
            {t('provider.commercial')}
          </Typography>
          {commercialPolicyItems.map((item) => (
            <TextInputItem key={item.name} item={item} {...formCtrl} />
          ))}
          <DateInput2
            className={formClasses.control}
            label={t('provider.insuranceExpires')}
            name="CommercialInsuranceExpiresOn"
            control={control}
            errors={formState.errors}
            minDate={minDate}
            maxDate={maxDate}
            required
          />
          <SelectMenu2
            className={formClasses.control}
            label={t('provider.insuranceStrength')}
            name="CommercialInsuranceStrengthID"
            values={insuranceStrengths}
            valueAttr="insuranceStrengthID"
            labelAttr="insuranceStrengthName"
            control={control}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
            required
          />

          <Typography className={formClasses.subtitle}>
            {t('provider.auto')}
          </Typography>
          {autoPolicyItems.map((item) => (
            <TextInputItem key={item.name} item={item} {...formCtrl} />
          ))}
          <DateInput2
            className={formClasses.control}
            label={t('provider.insuranceExpires')}
            name="AutoInsuranceExpiresOn"
            control={control}
            errors={formState.errors}
            minDate={minDate}
            maxDate={maxDate}
            required
          />
          <SelectMenu2
            className={formClasses.control}
            label={t('provider.insuranceStrength')}
            name="AutoInsuranceStrengthID"
            values={insuranceStrengths}
            valueAttr="insuranceStrengthID"
            labelAttr="insuranceStrengthName"
            control={control}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
            required
          />
          <SelectMenu2
            className={formClasses.control}
            label={t('provider.insuranceCoverageType')}
            name="AutoInsuranceCoverageTypeID"
            values={limitTypes}
            valueAttr="limitTypeID"
            labelAttr="limitTypeName"
            required={true}
            control={control}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
          />
          <CheckboxInput2
            label={t('provider.hasWorkersComp')}
            name="HasWorkersComp"
            className={formClasses.control}
            required={false}
            control={control}
          />
        </LocalizationProvider>
      </OnboardForm>
    );
  },
);

export default InsuranceForm;
